import React, { CSSProperties, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useLocation } from 'react-router-dom';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppSelector } from 'hooks';

import { useSupportContext } from './SupportContext';
import { Message, PlayerContext, Ticket, useSupportApi } from './useSupportApi';

type SupportChatProps = {
  ticket: Ticket;
  onBack: () => void;
};

const SupportChat = ({ ticket, onBack }: SupportChatProps) => {
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const { getMessages, sendMessage, reopenTicket, uploadFile } = useSupportApi();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { lastNotification, markTicketRead } = useSupportContext();
  // Location tracking for admin visibility — captured at send time on every
  // reply so support can see how the player moved through the app during
  // the conversation. Mirrors the shape used at ticket creation (see
  // SupportLanding.handleCreate) so the admin console can render one type
  // for both. Reading location/view/state is O(1) — nothing async or heavy
  // fires here; the object is built inline just before the sendMessage call.
  const location = useLocation();
  const { activeView, activeTableInfo } = useContext(ViewManagerContext);
  const game = useAppSelector((state) => state.currentTable.game);
  const captureContext = useCallback((): PlayerContext => {
    const g = game as any;
    const ctx: PlayerContext = {
      url: location.pathname + location.search,
      view: activeView,
      timestamp: new Date().toISOString()
    };
    if (activeTableInfo?.gameID) {
      ctx.gameID = activeTableInfo.gameID;
      ctx.gameType = activeTableInfo.gameType;
    }
    if (g?.gameNo) ctx.gameNo = String(g.gameNo);
    if (g?.gameID) ctx.gameID = String(g.gameID);
    if (g?.game) ctx.gameType = g.game;
    if (g?.blindLevels) ctx.blindLevels = g.blindLevels;
    if (g?.hand) ctx.hand = String(g.hand);
    return ctx;
  }, [location.pathname, location.search, activeView, activeTableInfo, game]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Live ticket status. The ticket prop is only a snapshot from when the panel
  // was opened; an agent can resolve/close the ticket while the chat is open,
  // so we keep status in state and refresh it from each poll.
  const [status, setStatus] = useState(ticket.status);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Ref on the messages scroll container so the auto-scroll effect can
  // check whether the player has scrolled UP to read history before
  // deciding to yank them back to the bottom on the next poll tick.
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  // Tracks whether the initial jump-to-latest for THIS chat session has
  // already fired. Stays true until the component unmounts / remounts
  // (opening a different ticket triggers a full remount because
  // SupportPanel keys by ticketId).
  const didInitialAutoScrollRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMessages = async () => {
    try {
      const res = await getMessages(ticket.ticketId);
      if (res?.messages) {
        setMessages(res.messages);
        setError('');
      }
      // getMessages echoes the current ticket status; pick up an agent-side
      // resolve/close so the banner and reply-reopen flow stay in sync without
      // the player having to close and reopen the panel.
      if (res?.status && res.status !== ticket.status) {
        ticket.status = res.status;
        setStatus(res.status);
      }
    } catch {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setStatus(ticket.status);
    fetchMessages();
    markTicketRead(ticket.ticketId);
    // Poll every 10s to match the Unity client's reply cadence. WebSocket push
    // (below) still delivers replies instantly; this is the fallback.
    pollRef.current = setInterval(fetchMessages, 10000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.ticketId]);

  // Fetch immediately when a real-time WS notification arrives for this ticket
  useEffect(() => {
    if (lastNotification?.ticketId === ticket.ticketId) {
      fetchMessages();
      // The player is actively viewing this ticket, so the reply that just
      // arrived is already read — don't let it re-flag the ticket in the list
      // or bump the header badge.
      markTicketRead(ticket.ticketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastNotification]);

  useEffect(() => {
    if (messages.length === 0) return;
    // Two scroll modes (mirrors the admin SupportTickets logic):
    //   1. Initial jump — first messages payload after opening the chat
    //      lands at the bottom instantly so the player sees the latest
    //      exchange without a smooth-scroll animation on mount.
    //   2. Follow-up — background 10 s poll or the player's own reply
    //      hitting local state. Only re-scroll if the player is already
    //      near the bottom; if they've scrolled UP to back-read history,
    //      leave them there. Fixes the "getting yanked to the latest chat
    //      every few seconds while back-reading" complaint.
    const scroller = messagesScrollRef.current;
    if (!scroller) return;
    if (!didInitialAutoScrollRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'auto' });
      didInitialAutoScrollRef.current = true;
      return;
    }
    const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    if (distanceFromBottom < 120) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Reopen a resolved/closed ticket. Returns true once the ticket is open
  // (treating an "already open" response as success). Keeps both the prop
  // snapshot and local status in sync.
  const tryReopen = async (): Promise<boolean> => {
    try {
      const res = await reopenTicket(ticket.ticketId);
      if (res?.error && !/already open/i.test(res.error)) return false;
      ticket.status = 'open';
      setStatus('open');
      return true;
    } catch {
      return false;
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setSending(true);
    setError('');
    setInput('');
    try {
      // Reopen up front when we already know the ticket is resolved/closed.
      if (status === 'resolved' || status === 'closed') {
        if (!(await tryReopen())) {
          setError('Failed to reopen ticket');
          setInput(text);
          return;
        }
      }

      const ctx = captureContext();
      let res = await sendMessage(ticket.ticketId, text, undefined, ctx);

      // The agent may have closed the ticket since our last poll, in which case
      // the API rejects the send with an error instead of a messageId. Reopen
      // and retry once so the reply isn't silently dropped — previously the
      // player had to close and reopen the panel before a message would send.
      if (!res?.messageId && /closed/i.test(res?.error || '')) {
        setStatus('closed');
        if (await tryReopen()) {
          res = await sendMessage(ticket.ticketId, text, undefined, ctx);
        }
      }

      if (res?.messageId) {
        setMessages((prev) => [
          ...prev,
          {
            messageId: res.messageId,
            ticketId: ticket.ticketId,
            sender: 'player',
            senderName: 'You',
            body: text,
            createdAt: new Date().toISOString()
          }
        ]);
      } else {
        setError(res?.error || 'Failed to send message');
        setInput(text);
      }
    } catch {
      setError('Failed to send message');
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || uploading) return;
    e.target.value = '';

    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum 10MB.');
      return;
    }

    setUploading(true);
    setError('');
    try {
      // Same reopen-first guard as text replies: an attachment sent to a
      // resolved/closed ticket would otherwise be rejected and lost.
      if ((status === 'resolved' || status === 'closed') && !(await tryReopen())) {
        setError('Failed to reopen ticket');
        return;
      }
      const attachment = await uploadFile(ticket.ticketId, file);
      const res = await sendMessage(ticket.ticketId, '', attachment, captureContext());
      if (res?.messageId) {
        setMessages((prev) => [
          ...prev,
          {
            messageId: res.messageId,
            ticketId: ticket.ticketId,
            sender: 'player',
            senderName: 'You',
            body: '',
            createdAt: new Date().toISOString(),
            attachmentName: file.name,
            attachmentType: file.type
          }
        ]);
      }
    } catch {
      setError('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isResolved = status === 'resolved' || status === 'closed';

  return (
    <div style={containerStyle}>
      {/* Ticket header */}
      <div style={headerStyle}>
        <button style={backBtnStyle} onClick={onBack} aria-label='Back'>
          <ArrowBackIcon style={{ fontSize: '1.1rem' }} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={subjectStyle}>{ticket.subject || ticket.category}</div>
          <div style={statusRowStyle}>
            <span
              style={{
                ...statusDotStyle,
                background: isResolved ? '#628B48' : '#E98C26'
              }}
            />
            <span
              style={{
                ...statusTextStyle,
                color: isResolved ? '#628B48' : 'rgba(212,168,83,0.7)'
              }}>
              {status}
            </span>
          </div>
        </div>
      </div>

      {/* Amber divider */}
      <div style={dividerStyle} />

      {/* Messages */}
      <div ref={messagesScrollRef} style={messagesContainerStyle}>
        {loading && <div style={emptyStyle}>Loading messages...</div>}
        {error && <div style={errorStyle}>{error}</div>}
        {!loading && messages.length === 0 && (
          <div style={emptyStyle}>No messages yet. Describe your issue below.</div>
        )}
        {messages.map((msg) => {
          const isMine = (msg as any).senderGUID === playerGUID || msg.sender === 'player';
          return (
            <div
              key={msg.messageId}
              style={{
                ...msgRowStyle,
                justifyContent: isMine ? 'flex-end' : 'flex-start'
              }}>
              {/* Agent avatar — anonymised: every agent shows a static "HJ"
                  instead of their real name's first initial, so the client
                  never sees which individual agent is replying. System
                  messages keep their own single-letter marker to match the
                  "System" label rendered in the bubble below. */}
              {!isMine && (
                <div style={agentAvatarStyle}>{msg.sender === 'system' ? 'S' : 'HJ'}</div>
              )}

              <div
                style={{
                  ...bubbleStyle,
                  ...(isMine ? playerBubbleStyle : agentBubbleStyle)
                }}>
                {!isMine && (
                  <div style={senderNameStyle}>
                    {msg.sender === 'system' ? 'System' : 'HJ Agent'}
                  </div>
                )}
                {msg.body && (
                  <div style={bodyTextStyle}>
                    {msg.messageType === 'TICKET_CLOSED'
                      ? // Client-facing text — deliberately generic. The server
                        // stores the closing agent's real display name in
                        // `body` for the admin console; the player must not
                        // see that identity, so swap in a static line here.
                        'Ticket closed by HJ Agent.'
                      : renderMessageBody(msg.body, isMine)}
                  </div>
                )}
                {msg.attachmentName && (
                  <a
                    href={msg.attachmentUrl || '#'}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={attachmentLinkStyle}
                    onClick={(e) => {
                      if (!msg.attachmentUrl) e.preventDefault();
                    }}>
                    {msg.attachmentType?.startsWith('image/') && msg.attachmentUrl ? (
                      <img
                        src={msg.attachmentUrl}
                        alt={msg.attachmentName}
                        style={attachmentImageStyle}
                      />
                    ) : (
                      <span style={attachmentFileStyle}>
                        <InsertDriveFileIcon style={{ fontSize: '0.9rem' }} />
                        {msg.attachmentName}
                      </span>
                    )}
                  </a>
                )}
                <div
                  style={{
                    ...timeStyle,
                    textAlign: isMine ? 'right' : 'left'
                  }}>
                  {new Date(msg.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Resolved banner */}
      {isResolved && <div style={resolvedBannerStyle}>Ticket resolved · Reply to reopen</div>}

      {/* Input bar */}
      <div style={inputBarStyle}>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/*,.pdf,.txt,.csv,.xlsx,.docx'
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <button
          style={attachBtnStyle}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title='Attach file'>
          <AttachFileIcon style={{ fontSize: '1.05rem' }} />
        </button>
        <textarea
          style={textareaStyle}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={uploading ? 'Uploading...' : 'Send a message...'}
          rows={1}
          disabled={uploading}
        />
        <button
          style={{
            ...sendBtnStyle,
            opacity: input.trim() && !sending ? 1 : 0.4
          }}
          onClick={handleSend}
          disabled={!input.trim() || sending}>
          SEND
        </button>
      </div>
    </div>
  );
};

export default SupportChat;

/* ── Layout ── */

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  flex: 1
};

/* ── Ticket header ── */

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  padding: '0.7rem 1rem',
  background: 'rgba(212,168,83,0.04)',
  flexShrink: 0
};

const backBtnStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '7px',
  color: 'rgba(212,168,83,0.7)',
  padding: '0.3rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.12s ease'
};

const subjectStyle: CSSProperties = {
  fontSize: '0.88rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.9)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const statusRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
  marginTop: '0.12rem'
};

const statusDotStyle: CSSProperties = {
  width: '5px',
  height: '5px',
  borderRadius: '50%',
  flexShrink: 0
};

const statusTextStyle: CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  textTransform: 'capitalize',
  letterSpacing: '0.04em'
};

const dividerStyle: CSSProperties = {
  height: '1px',
  background:
    'linear-gradient(90deg, transparent, rgba(212,168,83,0.25) 30%, rgba(212,168,83,0.25) 70%, transparent)',
  flexShrink: 0
};

/* ── Messages ── */

const messagesContainerStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '0.9rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem'
};

const emptyStyle: CSSProperties = {
  textAlign: 'center',
  color: 'rgba(255,255,255,0.3)',
  fontSize: '0.82rem',
  padding: '2.5rem 0',
  lineHeight: 1.5
};

const errorStyle: CSSProperties = {
  textAlign: 'center',
  color: '#e05555',
  fontSize: '0.78rem',
  padding: '0.5rem',
  background: 'rgba(220,50,50,0.08)',
  borderRadius: '8px'
};

const msgRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '0.45rem',
  width: '100%'
};

/* Agent avatar circle */
const agentAvatarStyle: CSSProperties = {
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  background: 'rgba(212,168,83,0.12)',
  border: '1px solid rgba(212,168,83,0.25)',
  color: 'rgba(212,168,83,0.85)',
  fontSize: '0.65rem',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const bubbleStyle: CSSProperties = {
  maxWidth: '78%',
  borderRadius: '12px',
  padding: '0.55rem 0.8rem',
  fontSize: '0.84rem',
  lineHeight: 1.45,
  wordBreak: 'break-word'
};

/* Player (right-aligned): subtle amber tint */
const playerBubbleStyle: CSSProperties = {
  background: 'rgba(233,140,38,0.16)',
  border: '1px solid rgba(233,140,38,0.22)',
  borderBottomRightRadius: '4px',
  color: 'rgba(255,255,255,0.92)'
};

/* Agent (left-aligned): dark gray */
const agentBubbleStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderBottomLeftRadius: '4px',
  color: 'rgba(255,255,255,0.9)'
};

const senderNameStyle: CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 700,
  color: 'rgba(212,168,83,0.85)',
  letterSpacing: '0.03em',
  marginBottom: '0.2rem',
  textTransform: 'uppercase'
};

const bodyTextStyle: CSSProperties = {
  whiteSpace: 'pre-wrap'
};

// URL pattern for auto-linkification of message bodies. Matches http:// and
// https:// URLs; deliberately conservative — no bare-domain matching (would
// false-positive on words with dots), no www.-only matching (browsers won't
// resolve without a protocol), and no scheme-less fragments. Trailing
// punctuation (. , ) ; : !) is stripped from the match end so a URL at the
// end of a sentence — "check https://example.com." — doesn't include the
// period in the anchor href.
const URL_PATTERN = /(https?:\/\/[^\s<>"]+[^\s<>"',.;:!?)])/g;

// Split a message body into text + anchor React nodes. Applied to the
// player-facing render only — admin-sent messages that include a shared URL
// (deep-links to a specific ticket / message, external doc links, cashier
// help pages) become clickable and open in a new tab with rel="noopener
// noreferrer" so the poker app tab stays in place and the linked origin
// can't reach back through window.opener.
function renderMessageBody(body: string, isMine: boolean): React.ReactNode {
  // `split` with a capturing regex interleaves matches with the surrounding
  // text: [text0, url1, text1, url2, text2, ...]. Every odd index is a
  // matched URL; every even index is plain text. This avoids the stateful
  // `lastIndex` gotcha with a /g regex (calling .test() in a loop would
  // otherwise return alternating true/false values on the same input).
  const parts = body.split(URL_PATTERN);
  if (parts.length === 1) return body;
  return parts.map((part, i) => {
    if (!part) return null;
    const isUrl = i % 2 === 1;
    if (!isUrl) return part;
    return (
      <a
        key={i}
        href={part}
        target='_blank'
        rel='noopener noreferrer'
        style={{
          // Player's own bubble uses a dark background; the agent bubble is
          // lighter — colour the link so it contrasts against both without
          // fighting the surrounding text. Underline signals "clickable" on
          // both sides.
          color: isMine ? '#8FCEFF' : '#3B82F6',
          textDecoration: 'underline',
          wordBreak: 'break-all'
        }}
        onClick={(e) => e.stopPropagation()}>
        {part}
      </a>
    );
  });
}

const timeStyle: CSSProperties = {
  fontSize: '0.58rem',
  color: 'rgba(255,255,255,0.25)',
  marginTop: '0.25rem'
};

/* ── Resolved banner ── */

const resolvedBannerStyle: CSSProperties = {
  padding: '0.45rem 1rem',
  background: 'rgba(98,139,72,0.1)',
  borderTop: '1px solid rgba(98,139,72,0.2)',
  color: '#628B48',
  fontSize: '0.7rem',
  fontWeight: 600,
  textAlign: 'center',
  letterSpacing: '0.03em',
  flexShrink: 0
};

/* ── Input bar ── */

const inputBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '0.4rem',
  padding: '0.65rem 0.85rem',
  borderTop: '1px solid rgba(212,168,83,0.1)',
  background: 'rgba(0,0,0,0.2)',
  flexShrink: 0
};

const textareaStyle: CSSProperties = {
  flex: 1,
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '0.84rem',
  fontFamily: "'Outfit', sans-serif",
  padding: '0.55rem 0.7rem',
  resize: 'none',
  outline: 'none',
  maxHeight: '90px',
  lineHeight: 1.4
};

const sendBtnStyle: CSSProperties = {
  background: 'linear-gradient(135deg, #E98C26 0%, #c97010 100%)',
  border: 'none',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '0.7rem',
  fontWeight: 700,
  fontFamily: "'Outfit', sans-serif",
  letterSpacing: '0.06em',
  padding: '0.55rem 0.9rem',
  cursor: 'pointer',
  flexShrink: 0,
  minHeight: '38px',
  transition: 'opacity 0.15s ease'
};

const attachBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(255,255,255,0.35)',
  cursor: 'pointer',
  padding: '0.35rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  flexShrink: 0,
  transition: 'color 0.12s ease'
};

const attachmentLinkStyle: CSSProperties = {
  display: 'block',
  textDecoration: 'none',
  marginTop: '0.3rem'
};

const attachmentImageStyle: CSSProperties = {
  maxWidth: '200px',
  maxHeight: '150px',
  borderRadius: '8px',
  objectFit: 'cover'
};

const attachmentFileStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
  fontSize: '0.75rem',
  color: '#E98C26',
  padding: '0.3rem 0.5rem',
  background: 'rgba(233,140,38,0.1)',
  borderRadius: '6px',
  border: '1px solid rgba(233,140,38,0.22)'
};
